import * as yup from 'yup';
import { AnyObjectSchema } from 'yup';
import {
  descriptionValidation,
  enabledValidation,
  imageValidation,
  regexColor,
  maxirestCodeValidation,
} from 'utils/yup';
import { TFunction } from 'react-i18next';

const maxFileSize = 50 * 1024; // 50 KB

export const CreateForm = (maxirestCodeIsRequired: boolean, t: TFunction): AnyObjectSchema => {
  return yup.object().shape({
    name: yup.string().required(t('schema-product-form-name-required')),
    description: descriptionValidation(t),
    price: yup
      .string()
      .required(t('schema-product-form-price-required'))
      .test('maxDigitsAfterDecimal', t('schema-product-form-price-test'), (number: any) =>
        /^\d+(\.\d{1,2})?$/.test(number),
      ),
    estimatedPreparationTime: yup
      .number()
      .min(0o1, t('schema-product-form-estimatedPreparationTime-min'))
      .max(59, t('schema-product-form-estimatedPreparationTime-max'))
      .nullable(true),
    maxirestCode: maxirestCodeValidation(maxirestCodeIsRequired, t),
    category: yup.string().required(t('schema-product-form-category-required')),
    isPromotion: yup.boolean().required(t('schema-product-form-isPromotion-required')),
    showInList: yup.boolean().required(t('schema-product-form-showInList-required')),
    logo: imageValidation('Logo', true, t),
    stock: yup.mixed().when('isClothing', {
      is: false,
      then: yup.number().required(t('schema-product-form-stock-required')),
    }),
    minimunStock: yup.mixed().when('isClothing', {
      is: false,
      then: yup.number().required(t('schema-product-form-minimun-stock-required')),
    }),
    minimumAmount: yup.mixed().when('suggestedType', {
      is: true,
      then: yup.number().required(t('schema-product-form-minimun-amount-required')),
    }),
    promotionBanner: yup.mixed().when('isPromotion', {
      is: true,
      then: imageValidation('Banner', true, t),
      otherwise: imageValidation('Banner', false, t),
    }),
    promotionBannerBackgroundColor: yup.mixed().when('isPromotion', {
      is: true,
      then: yup
        .string()
        .required(t('schema-product-form-promotionBannerBackgroundColor-required'))
        .matches(regexColor, t('schema-product-form-promotionBannerBackgroundColor-matches')),
    }),
    promotionBannerColorTitle: yup.mixed().when('isPromotion', {
      is: true,
      then: yup
        .string()
        .required(t('schema-product-form-promotionBannerColorTitle-required'))
        .matches(regexColor, t('schema-product-form-promotionBannerColorTitle-matches')),
    }),
    cloverLogo: imageValidation('Clover Logo', false, t, maxFileSize),
  });
};

export const EditForm = (maxirestCodeIsRequired: boolean, t: TFunction, productIdBeingEdited: string): any =>
  yup.object().shape({
    name: yup.string().required(t('schema-product-form-name-required')),
    description: descriptionValidation(t),
    price: yup
      .string()
      .required(t('schema-product-form-price-required'))
      .test('maxDigitsAfterDecimal', t(''), (number: any) => /^\d+(\.\d{1,2})?$/.test(number)), //revisar
    estimatedPreparationTime: yup
      .number()
      .min(0o1, t('schema-product-form-estimatedPreparationTime-min'))
      .max(59, t('schema-product-form-estimatedPreparationTime-max'))
      .nullable(true),
    maxirestCode: maxirestCodeValidation(maxirestCodeIsRequired, t),
    isPromotion: yup.boolean().required(t('schema-product-form-isPromotion-required')),
    showInList: yup.boolean().required(t('schema-product-form-showInList-required')),
    logo: imageValidation('Logo', true, t),
    enabled: enabledValidation(t),
    stock: yup.mixed().when('isClothing', {
      is: false,
      then: yup.number().required(t('schema-product-form-stock-required')),
    }),
    minimunStock: yup.mixed().when('isClothing', {
      is: false,
      then: yup.number().required(t('schema-product-form-minimun-stock-required')),
    }),
    isUsedForTomorrowBenefit: enabledValidation(t),
    promotionBanner: yup.mixed().when('isPromotion', {
      is: true,
      then: imageValidation('Banner', true, t),
      otherwise: imageValidation('Banner', false, t),
    }),
    suggestedType: yup.mixed().when('isSuggested', {
      is: true,
      then: yup.number().required(t('schema-product-form-suggested-type-required')),
    }),
    minimumAmount: yup.mixed().when('suggestedType', {
      is: true,
      then: yup.number().required(t('schema-product-form-minimun-amount-required')),
    }),
    promotionBannerBackgroundColor: yup.mixed().when('isPromotion', {
      is: true,
      then: yup
        .string()
        .required(t('schema-product-form-promotionBannerBackgroundColor-required'))
        .matches(regexColor, t('schema-product-form-promotionBannerBackgroundColor-matches')),
    }),
    promotionBannerColorTitle: yup.mixed().when('isPromotion', {
      is: true,
      then: yup
        .string()
        .required(t('schema-product-form-promotionBannerColorTitle-required'))
        .matches(regexColor, t('schema-product-form-promotionBannerColorTitle-matches')),
    }),
    cloverLogo: imageValidation('Clover Logo', false, t, maxFileSize),
    amountInStockAvailableForTomorrowBenefit: yup.mixed().when('isUsedForTomorrowBenefit', {
      is: true,
      then: yup
        .number()
        .min(1, t('schema-product-form-amountInStockAvailableForTomorrowBenefit-min-higher-that-one'))
        .test(
          'maxAmountForBenefit',
          t('schema-product-form-amountInStockAvailableForTomorrowBenefit-max-no-higher-than-stock'),
          (value, context) => {
            if (!value) return true;
            return value <= context.parent.stock;
          },
        )
        .required(t('schema-product-form-amountInStockAvailableForTomorrowBenefit-required')),
    }),
    tomorrowPlanIds: yup.mixed().when('isUsedForTomorrowBenefit', {
      is: true,
      then: yup
        .array()
        .of(yup.number())
        .required(t('schema-product-form-tomorrow-plan-id-is-required'))
        .min(1, t('schema-product-form-tomorrow-plan-id-min')),
    }),
    tomorrowBenefitDiscountPercentage: yup.mixed().when('isUsedForTomorrowBenefit', {
      is: true,
      then: yup
        .number()
        .positive(t('schema-discount-form-amount-positive'))
        .required(t('schema-discount-form-amount-required'))
        .min(1, t('schema-discount-form-amount-minimun'))
        .max(100, t('schema-discount-form-amount-max')),
    }),
    productIdsForTomorrowGlobalBenefit: yup.mixed().when('isUsedForTomorrowBenefit', {
      is: true,
      then: yup
        .array()
        .of(yup.string())
        .min(1, t('schema-product-form-tomorrowProductsForTomorrowGlobalBenefit-min-one'))
        .test(
          'isProductBeingEditedPresentOnArray',
          t('schema-product-form-tomorrowProductsForTomorrowGlobalBenefit-this-product'),
          (value) => {
            if (!value) return true;
            const isProductPresent = value.find((productId) => productId === productIdBeingEdited);
            return isProductPresent ? true : false;
          },
        )
        .required(t('schema-product-form-tomorrowProductsForTomorrowGlobalBenefit-required')),
    }),
  });
